import type {
  BatDau2faResponse,
  DoiEmailInput,
  DoiMatKhauInput,
  MaDuPhongResponse,
  MeResponse,
  TrangThai2fa,
  XacNhan2faInput,
  XacNhanMatKhauInput,
} from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { goiApi, type LoiApi } from '../../lib/api.js';

/**
 * Trang "Tài khoản của tôi".
 *
 * Hai endpoint này chỉ cần đăng nhập, không đòi quyền module nào: ai cũng được
 * đổi mật khẩu và email của **chính mình**.
 */

const GOC = '/api/v1/core';

const KHOA_ME = ['core', 'me'] as const;

export function useDoiMatKhau() {
  return useMutation<{ ok: true }, LoiApi, DoiMatKhauInput>({
    mutationFn: (input) =>
      goiApi<{ ok: true }>(`${GOC}/tai-khoan/mat-khau`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
  });
}

export function useDoiEmail() {
  const qc = useQueryClient();
  return useMutation<MeResponse, LoiApi, DoiEmailInput>({
    mutationFn: (input) =>
      goiApi<MeResponse>(`${GOC}/tai-khoan/email`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    // Email hiện ở thẻ người dùng góc dưới, nên cập nhật luôn `me`.
    onSuccess: (du) => qc.setQueryData(KHOA_ME, du),
  });
}

/* ------------------------------------------------------------------ */
/* Xác thực hai bước                                                  */
/* ------------------------------------------------------------------ */

const KHOA_2FA = ['core', '2fa'] as const;

export function useTrangThai2fa() {
  return useQuery<TrangThai2fa>({
    queryKey: KHOA_2FA,
    queryFn: () => goiApi<TrangThai2fa>(`${GOC}/2fa`),
    staleTime: 0,
  });
}

/**
 * Bắt đầu một lượt bật: sinh secret, mã QR và phiếu.
 *
 * `useMutation` chứ không `useQuery` dù là phép đọc: mỗi lượt gọi sinh một
 * secret **khác**, nên nó không được chạy lại tự động theo vòng đời của query
 * (focus lại cửa sổ, nối lại mạng) — mã QR đang hiện trên màn hình sẽ đổi dưới
 * tay người đang quét.
 */
export function useBatDau2fa() {
  return useMutation<BatDau2faResponse, LoiApi, void>({
    mutationFn: () => goiApi<BatDau2faResponse>(`${GOC}/2fa/bat-dau`, { method: 'POST' }),
  });
}

export function useXacNhan2fa() {
  const qc = useQueryClient();
  return useMutation<MaDuPhongResponse, LoiApi, XacNhan2faInput>({
    mutationFn: (input) =>
      goiApi<MaDuPhongResponse>(`${GOC}/2fa/xac-nhan`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    /*
     * Bật xong là `buocBat2fa` ở `/core/me` đổi, mà chính nó quyết định màn ép
     * bật còn hiện hay không — phải nạp lại, không chỉ nạp lại `KHOA_2FA`.
     */
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KHOA_ME });
      void qc.invalidateQueries({ queryKey: KHOA_2FA });
    },
  });
}

export function useTat2fa() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, LoiApi, XacNhanMatKhauInput>({
    mutationFn: (input) =>
      goiApi<{ ok: true }>(`${GOC}/2fa/tat`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KHOA_ME });
      void qc.invalidateQueries({ queryKey: KHOA_2FA });
    },
  });
}

export function useSinhLaiMaDuPhong() {
  const qc = useQueryClient();
  return useMutation<MaDuPhongResponse, LoiApi, XacNhanMatKhauInput>({
    mutationFn: (input) =>
      goiApi<MaDuPhongResponse>(`${GOC}/2fa/ma-du-phong`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KHOA_2FA });
    },
  });
}

/** Quản trị gỡ 2FA của người khác. Dùng ở bảng Thành viên của module 10. */
export function useGo2faCuaNguoiKhac() {
  const qc = useQueryClient();
  return useMutation<{ id: string; email: string; daBat: boolean }, LoiApi, string>({
    mutationFn: (userId) =>
      goiApi<{ id: string; email: string; daBat: boolean }>(
        `${GOC}/nhan-su/${encodeURIComponent(userId)}/2fa/go`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      /* Cột "Xác thực hai bước" của bảng Thành viên đọc từ danh sách của module 10. */
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}
